import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, FunnelChart, Funnel, LabelList, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Loader2, Users, Target, Gauge, Eye, TrendingUp, Sparkles } from "lucide-react";
import { clientDashboard } from "@/lib/api";

const BRAND = "#2563EB";
const nf = (n) => (n ?? 0).toLocaleString();

export default function ClientDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { clientDashboard(token).then(setData).catch(() => setErr(true)); }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="client-dash-error">Dashboard not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const t = data.totals;
  const bc = data.client.brand_color || BRAND;
  const funnelData = data.funnel.map((s, i) => ({ name: s.stage, value: s.value, fill: [bc, "#7C3AED", "#0EA5E9", "#10B981"][i % 4] }));

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden" data-testid="client-dashboard">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" style={{ borderTop: `3px solid ${bc}` }}>
        <div className="flex items-center gap-3">
          {data.client.logo_url ? (
            <img src={data.client.logo_url} alt={data.client.name} data-testid="client-logo" className="w-9 h-9 rounded-xl object-contain border border-slate-100 bg-white" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: bc }}><Sparkles size={17} /></span>
          )}
          <div>
            <p className="font-heading font-bold text-slate-900 leading-tight">{data.client.name}</p>
            <p className="text-[11px] text-slate-400">{data.client.website}</p>
          </div>
        </div>
        <span className="text-xs text-slate-400">Powered by WebsiteTech.pro</span>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Your Website Performance</h1>
            <p className="text-slate-500 mt-1 text-sm">Last 30 days · leads & audits are live; traffic is projected while analytics connect.</p>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-1 rounded">Demo traffic</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi icon={Eye} label="Visitors" value={nf(t.visitors)} accent={bc} />
          <Kpi icon={Users} label="Leads" value={nf(t.leads)} accent={bc} />
          <Kpi icon={Target} label="Win Rate" value={`${t.win_rate}%`} accent="#10B981" />
          <Kpi icon={Gauge} label="Avg Score" value={t.avg_score} accent="#F59E0B" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Panel title="Lead Funnel" subtitle="Your pipeline" testid="client-funnel">
            <div className="h-64">
              {t.leads === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
                  <Users size={26} className="mb-2 opacity-50" />
                  <p className="text-sm">No leads captured yet.</p>
                  <p className="text-xs mt-1">They'll appear here as visitors convert.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <Tooltip />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      <LabelList position="right" fill="#334155" stroke="none" dataKey="name" className="text-xs" />
                      <LabelList position="left" fill="#94a3b8" stroke="none" dataKey="value" className="text-xs" />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>

          <Panel title="Traffic & Leads" subtitle="Daily trend" className="lg:col-span-2" testid="client-trend">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.trend} margin={{ left: -6, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="cv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.35} /><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient>
                    <linearGradient id="cl" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={bc} stopOpacity={0.35} /><stop offset="95%" stopColor={bc} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} width={42} />
                  <Tooltip />
                  <Area type="monotone" dataKey="visitors" stroke="#0EA5E9" strokeWidth={2} fill="url(#cv)" name="Visitors" />
                  <Area type="monotone" dataKey="leads" stroke={bc} strokeWidth={2} fill="url(#cl)" name="Leads" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Top pages */}
        <Panel title="Top Pages" subtitle="Most-visited pages on your site" className="mt-6" testid="client-top-pages">
          <div className="space-y-2.5">
            {data.top_pages?.map((p, i) => {
              const max = data.top_pages[0].views || 1;
              return (
                <div key={i} className="flex items-center gap-3" data-testid="client-page-row">
                  <span className="text-sm font-medium text-slate-700 w-28 shrink-0 truncate">{p.path}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max(6, (p.views / max) * 100)}%`, background: bc }} />
                  </div>
                  <span className="text-xs text-slate-500 w-16 text-right shrink-0">{nf(p.views)}</span>
                  <span className="text-xs text-slate-400 w-16 text-right shrink-0 hidden sm:block">{p.avg_time}</span>
                </div>
              );
            })}
          </div>
        </Panel>

        <div className="mt-6 rounded-2xl p-6 flex items-center gap-4 flex-wrap" style={{ background: `${bc}0d`, border: `1px solid ${bc}1a` }}>
          <TrendingUp size={22} style={{ color: bc }} />
          <p className="text-sm text-slate-600 flex-1">Want to turn more of these visitors into leads? WebsiteTech can optimize your site's SEO, speed and conversion.</p>
          <a href="/" target="_blank" rel="noopener noreferrer" className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white" style={{ background: bc }}>Talk to WebsiteTech</a>
        </div>
      </main>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, accent = BRAND }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`client-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${accent}14`, color: accent }}><Icon size={16} /></span>
    <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
    <p className="text-[11px] text-slate-400 font-medium mt-0.5">{label}</p>
  </div>
);

const Panel = ({ title, subtitle, children, className = "", testid }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl p-5 ${className}`} data-testid={testid}>
    <div className="mb-4">
      <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
      {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
    </div>
    {children}
  </div>
);
